
import {
    BetItem,
    computed,
    defineComponent,
    Emitter,
    inject,
    onBeforeMount,
    onBeforeUnmount,
    onMounted,
    ref,
    UserInfo,
    watch
} from 'vue'
import { useViewPort } from './composables/useViewPort'
import { useStore } from './store'
import AppLoader from '@/components/app-loader.vue'
import { EventBuss, SOCKET_MESSAGE } from './types/global'
import { AppMutation } from './store/types'
import screenfull from 'screenfull'
import { getDeviceOrientation, isMobile as isAMobile } from './utils'
import {
    MsgBoxLanguage,
    MsgBoxSetting,
    MsgBoxSecurity,
    MsgBoxAgree,
    MsgBoxChip,
    MsgBoxDemo,
    MsgGameRule,
    MsgRecord,
    MsgBoxMessage,
    MsgBoxTip,
    MsgBoxTipConcent,
    MsgBoxConfirmBooking,
    MsgBoxMediaSetting,
    MsgBoxRequiredChangePassword,
    MsgPrompt,
    MsgBoxGamePlay
} from '@/components/dialog'
import {
    DialogBoxName,
    MessageBoxActionType,
    MessageBoxEvent
} from './types/MessageBox'
import { useRoute, useRouter } from 'vue-router'
import { ROUTES } from './router'
import { MJWebsocket } from './utils/websocket'
import audioPlayer from './utils/sounds'
import { useI18n } from 'vue-i18n'
import { useAssetLoader } from './composables/useAssetLoader'
import { Dictionary } from './types/dictionary'

export default defineComponent({
    components: {
        'app-loader': AppLoader,
        'msg-box-language': MsgBoxLanguage,
        'msg-box-setting': MsgBoxSetting,
        'msg-box-security': MsgBoxSecurity,
        'msg-box-agree': MsgBoxAgree,
        'msg-box-chips': MsgBoxChip,
        'msg-box-demo': MsgBoxDemo,
        'msg-box-rule': MsgGameRule,
        'msg-box-record': MsgRecord,
        'msg-box-message': MsgBoxMessage,
        'msg-box-tip': MsgBoxTip,
        'msg-box-tip-concent': MsgBoxTipConcent,
        'msg-box-confirm-booking': MsgBoxConfirmBooking,
        'msg-box-media-setting': MsgBoxMediaSetting,
        'msg-required-change-password': MsgBoxRequiredChangePassword,
        'msg-prompt': MsgPrompt,
        'msg-box-game-play': MsgBoxGamePlay
    },
    setup() {
        const appRoot = ref<HTMLElement | undefined>(undefined)
        const store = useStore()
        const emitter = inject('emitter') as Emitter
        const route = useRoute()
        const router = useRouter()
        const { t } = useI18n()
        let isPageBlured = false
        let lastPageVisibleEmit = 0

        const _ws: MJWebsocket = new MJWebsocket(emitter)

        const toastMessage = ref<string>('This is Toast')
        const toastMessageCounter = ref<number>(0)

        const dialogCollection = ref<string[]>([])

        const showLanguageDialogBox = ref<boolean>(false)
        const showSettingDialogBox = ref<boolean>(false)
        const showSecurityDialogBox = ref<boolean>(false)
        const showAgreeDialogBox = ref<boolean>(false)
        const showChipsDialogBox = ref<boolean>(false)
        const showDemoLogin = ref<boolean>(false)
        const showRuleDialogBox = ref<boolean>(false)
        const showRecordDialogBox = ref<boolean>(false)
        const showMessageDialogBox = ref<boolean>(false)
        const showMessageDialogTipBox = ref<boolean>(false)
        const showMessageDialogTipBoxConcent = ref<boolean>(false)
        const showMessageDialogVipBox = ref<boolean>(false)
        const showMessageDialogMediaSettingBox = ref<boolean>(false)
        const showRequiredChangePassword = ref(false)
        const showMessageDialogPrompt = ref(false)
        const showGamePlayDialogBox = ref(false)
        let clientTimeOutStarted = false
        let clientTimeOutSession: number | boolean = false

        let toastMessageInterval: boolean | number = false

        const showMainView = ref(true)

        const { loadImages } = useAssetLoader()

        //#region Computed Helpers
        const isMobile = computed((): boolean => store.getters['isMobile'])
        const deviceOrientation = computed(
            (): 'landscape' | 'portrait' => store.getters['deviceOrientation']
        )
        //#endregion

        const { windowResize, initialize } = useViewPort(appRoot.value)

        //#region Vue Helpers
        onBeforeMount(() => {
            // preload image assets
            loadImages()

            window.addEventListener('resize', handleResizeWindow)
            window.addEventListener(
                'visibilitychange',
                handlePageVisibilityChange
            )

            window.addEventListener('blur', handlePageBlur)
            window.addEventListener('focus', handlePageFocus)
            emitter.on(EventBuss.TOAST_MESSAGE, handleToastMessage)
            emitter.on(EventBuss.DIALOG, handleDialogEvent)
            emitter.on(EventBuss.REQUEST_FULL_SCREEN, requestFullScreen)
            emitter.on(EventBuss.SOCKET_CONNECT, connectToWebsocket)
            emitter.on(EventBuss.SOCKET_CLOSE, handleCloseSocket)
            emitter.on(EventBuss.PAGE_ON_VISIBLE, handlePageVisibleReconnect)
            // emitter.on(EventBuss.START_CLIENT_TIME_OUT, startClientTimeOut)

            if (isMobile.value) {
                window.addEventListener(
                    'orientationchange',
                    handleOrientationChange
                )
            }
            initWebSocket()
        })

        onMounted(() => {
            // const lastBets: Dictionary<BetItem[]> = new Dictionary()

            // lastBets.Add('12', [{ type: 1, amount: 30 }])
            // lastBets.Add('142', [{ type: 1, amount: 100 }])
            // lastBets.Add('141', [{ type: 1, amount: 900 }])
            // lastBets.Add('11', [
            //     { type: 1, amount: 300 },
            //     { type: 5, amount: 100 }
            // ])

            // // lastBets.Add('141', [{ type: 1, amount: 900 }])

            // const bet = lastBets.Remove('141')
            // bet.push({ type: 3, amount: 50 })

            // lastBets.Add('141', bet)

            // console.log(lastBets.LastItem())
            // // console.log(lastBets.Keys())

            handleResizeWindow()
            if (!audioPlayer.isLoaded) {
                audioPlayer.isOn = canPlaySound.value ? 1 : 0
                audioPlayer.LoadSounds()
            }

            if (
                token.value &&
                route.name !== ROUTES.LOGIN &&
                route.name !== ROUTES.SERVER_SELECTION &&
                !_ws.isConnected &&
                !_ws.isConnecting
            ) {
                connectToWebsocket()
            }
        })

        onBeforeUnmount(() => {
            emitter.off(EventBuss.TOAST_MESSAGE, handleToastMessage)
            emitter.off(EventBuss.DIALOG, handleDialogEvent)
            emitter.off(EventBuss.REQUEST_FULL_SCREEN, requestFullScreen)
            emitter.off(EventBuss.SOCKET_CONNECT, connectToWebsocket)
            emitter.off(EventBuss.SOCKET_CLOSE, handleCloseSocket)
            emitter.off(EventBuss.PAGE_ON_VISIBLE, handlePageVisibleReconnect)
            // emitter.off(EventBuss.START_CLIENT_TIME_OUT, startClientTimeOut)

            window.removeEventListener(
                'visibilitychange',
                handlePageVisibilityChange
            )

            window.removeEventListener('blur', handlePageBlur)
            window.removeEventListener('focus', handlePageFocus)

            if (isMobile.value) {
                window.removeEventListener(
                    'orientationchange',
                    handleOrientationChange
                )
            }

            _ws.shutdown()
        })
        //#endregion

        //#region Helpers

        const emitPageVisible = () => {
            const now = Date.now()
            if (now - lastPageVisibleEmit < 500) return
            lastPageVisibleEmit = now
            emitter.emit(EventBuss.PAGE_ON_VISIBLE)
        }

        const handlePageBlur = () => {
            isPageBlured = true
        }

        const handlePageFocus = () => {
            if (
                isPageBlured &&
                document.visibilityState === 'visible'
            ) {
                isPageBlured = false
                emitPageVisible()
                if (music.value) audioPlayer.PlayMusic()
            }
        }

        const handlePageVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                isPageBlured = false
                emitPageVisible()

                if (music.value) audioPlayer.PlayMusic()
            } else {
                audioPlayer.StopMusic()
            }
        }

        const handleResizeWindow = () => {
            windowResize(appRoot.value)
            emitter.emit(EventBuss.WINDOW_RESIZED)
        }

        const handleToastMessage = (_message: string) => {
            toastMessageCounter.value = 9
            toastMessage.value = _message
            if (typeof toastMessageInterval !== 'number') {
                toastMessageInterval = setInterval(() => {
                    toastMessageCounter.value -= 1
                    if (
                        toastMessageCounter.value <= 0 &&
                        typeof toastMessageInterval === 'number'
                    ) {
                        clearInterval(toastMessageInterval)
                        toastMessageInterval = false
                        toastMessage.value = ''
                    }
                }, 150)
            }
        }

        const initSound = () => {
            if (!isUserInteracted.value) {
                store.commit(AppMutation.SET_USER_INTERACTION)
                audioPlayer.Start()

                if (
                    route.name !== ROUTES.LOGIN &&
                    route.name !== ROUTES.SERVER_SELECTION &&
                    music.value
                )
                    audioPlayer.PlayMusic()
                else audioPlayer.StopMusic()
            }

            // const _evt: MessageBoxEvent = {
            //     type: MessageBoxActionType.open,
            //     name: 'game-play'
            // }
            // emitter.emit(EventBuss.DIALOG, _evt)
        }

        const handleOrientationChange = () => {
            if (isAMobile()) {
                showMainView.value = false
                setTimeout(() => {
                    handleResizeWindow()
                    showMainView.value = true
                }, 500)
            }
        }

        const requestFullScreen = () => {
            if (screenfull.isEnabled) {
                const docEl = document.getElementById('app') as HTMLElement
                if (!screenfull.isFullscreen) {
                    screenfull
                        .request(docEl, { navigationUI: 'hide' })
                        .then(() => {
                            store.commit(
                                AppMutation.SET_FULL_SCREEN,
                                screenfull.isFullscreen
                            )
                            // initialize()
                            // windowResize(appRoot.value)
                        })
                } else {
                    screenfull.exit().then(() => {
                        store.commit(
                            AppMutation.SET_FULL_SCREEN,
                            screenfull.isFullscreen
                        )
                        // initialize()
                        // windowResize(appRoot.value)
                    })
                }
            }
        }

        const handleDialogEvent = (e: MessageBoxEvent) => {
            if (e.type === MessageBoxActionType.close) {
                if (dialogCollection.value.includes(e.name)) {
                    const _i = dialogCollection.value.indexOf(e.name)
                    dialogCollection.value.splice(_i, 1)
                }

                if (e.name === 'lang') showLanguageDialogBox.value = false
                else if (e.name === 'setting')
                    showSettingDialogBox.value = false
                else if (e.name === 'security')
                    showSecurityDialogBox.value = false
                else if (e.name === 'agree') showAgreeDialogBox.value = false
                else if (e.name === 'chips') showChipsDialogBox.value = false
                else if (e.name === 'demo') showDemoLogin.value = false
                else if (e.name === 'rule') showRuleDialogBox.value = false
                else if (e.name === 'record') showRecordDialogBox.value = false
                else if (e.name === 'message')
                    showMessageDialogBox.value = false
                else if (e.name === 'tipbox')
                    showMessageDialogTipBox.value = false
                else if (e.name === 'tipbox-concent')
                    showMessageDialogTipBoxConcent.value = false
                else if (e.name === 'confirm-booking')
                    showMessageDialogVipBox.value = false
                else if (e.name === 'media-setting')
                    showMessageDialogMediaSettingBox.value = false
                else if (e.name === 'required-change-password')
                    showRequiredChangePassword.value = false
                else if (e.name === 'prompt')
                    showMessageDialogPrompt.value = false
                else if (e.name === 'game-play')
                    showGamePlayDialogBox.value = false
            } else if (e.type === MessageBoxActionType.open) {
                if (!dialogCollection.value.includes(e.name))
                    dialogCollection.value.push(e.name)
                if (e.name === 'lang') showLanguageDialogBox.value = true
                else if (e.name === 'setting') showSettingDialogBox.value = true
                else if (e.name === 'security')
                    showSecurityDialogBox.value = true
                else if (e.name === 'agree') showAgreeDialogBox.value = true
                else if (e.name === 'chips') showChipsDialogBox.value = true
                else if (e.name === 'demo') showDemoLogin.value = true
                else if (e.name === 'rule') showRuleDialogBox.value = true
                else if (e.name === 'record') showRecordDialogBox.value = true
                else if (e.name === 'message') showMessageDialogBox.value = true
                else if (e.name === 'tipbox')
                    showMessageDialogTipBox.value = true
                else if (e.name === 'tipbox-concent')
                    showMessageDialogTipBoxConcent.value = true
                else if (e.name === 'confirm-booking')
                    showMessageDialogVipBox.value = true
                else if (e.name === 'media-setting')
                    showMessageDialogMediaSettingBox.value = true
                else if (e.name === 'required-change-password')
                    showRequiredChangePassword.value = true
                else if (e.name === 'prompt')
                    showMessageDialogPrompt.value = true
                else if (e.name === 'game-play')
                    showGamePlayDialogBox.value = true
            }
        }

        const closeAllDialogs = () => {
            const _arr = [...dialogCollection.value]
            _arr.forEach((_m) => {
                const _ev: MessageBoxEvent = {
                    type: MessageBoxActionType.close,
                    name: _m as DialogBoxName
                }

                handleDialogEvent(_ev)
            })
        }
        //#endregion

        //#region MJSocket Helpers
        const buildWebsocketUrl = (): string => {
            let servicePath = ''

            if (serverLine.value > 0) {
                if (serverLine.value === 1) {
                    servicePath = `${process.env.API1}`
                } else if (serverLine.value === 2) {
                    servicePath = `${process.env.API2}`
                } else if (serverLine.value === 3) {
                    servicePath = `${process.env.API3}`
                }
            } else if (serverLine.value === -1) {
                servicePath = serverLineStr.value
            }

            if (!servicePath) return ''

            const protocol = servicePath.startsWith('https://')
                ? 'wss://'
                : 'ws://'

            const wsPath = servicePath.replace(/^https?:\/\//, protocol)
            return `${wsPath}/websocketUserServer/${token.value}`
        }

        const connectToWebsocket = () => {
            if (!token.value) return

            const wsUrl = buildWebsocketUrl()
            if (!wsUrl) {
                console.warn('WS: no server line configured')
                return
            }

            console.log('connect')
            _ws.connect(wsUrl)
        }

        const handlePageVisibleReconnect = () => {
            if (!token.value) return
            if (
                route.name === ROUTES.LOGIN ||
                route.name === ROUTES.SERVER_SELECTION
            ) {
                return
            }

            const wsUrl = buildWebsocketUrl()
            if (!wsUrl) return

            _ws.reconnectIfNeeded(wsUrl)
        }

        const endSession = () => {
            store.commit(AppMutation.CLEAR_ALL)
            handleCloseSocket()
            closeAllDialogs()
            audioPlayer.StopMusic()
            router.push({ name: ROUTES.LOGIN })
        }

        const initWebSocket = () => {
            _ws.OnMessage = (message: MessageEvent) => {
                if (typeof message.data === 'string') {
                    const objMessage = JSON.parse(message.data)

                    emitter.emit(EventBuss.SOCKET_MESSAGE, objMessage)

                    const _type = objMessage.type.toUpperCase()

                    // console.log(_type)

                    if (
                        _type === SOCKET_MESSAGE.USER_OUT ||
                        _type === SOCKET_MESSAGE.USER_OUT_BY
                    ) {
                        endSession()

                        const _evt: MessageBoxEvent = {
                            type: MessageBoxActionType.open,
                            name: 'message'
                        }

                        emitter.emit(EventBuss.DIALOG, _evt)

                        setTimeout(() => {
                            emitter.emit(EventBuss.HINT, objMessage.content)
                        }, 250)
                    } else if (_type === SOCKET_MESSAGE.MEMBER_AMOUNT) {
                        store.commit(
                            AppMutation.SET_COIN,
                            objMessage.data.amount
                        )
                    } else if (_type === SOCKET_MESSAGE.WS_EXPIRED) {
                        endSession()

                        const evt: MessageBoxEvent = {
                            type: MessageBoxActionType.open,
                            name: 'message'
                        }
                        emitter.emit(EventBuss.DIALOG, evt)
                        setTimeout(() => {
                            emitter.emit(
                                EventBuss.HINT,
                                t('prompts.socketnetworkerror')
                            )
                        }, 250)
                    } else if (
                        _type === SOCKET_MESSAGE.GUNNER_BET ||
                        _type === SOCKET_MESSAGE.BET
                    ) {
                        const _sDesk = objMessage.gameTable
                        if (_sDesk.expand.memberId === memberid.value) {
                            store.commit(AppMutation.SET_CLIENT_TIME_OUT, 10)
                        }
                    }
                }
            }
        }

        const handleCloseSocket = () => {
            _ws.close()
        }

        //#endregion

        const isFreeUser = computed((): boolean => store.getters['isFreeUser'])
        const showLoading = computed(
            (): boolean => store.getters['showLoading']
        )

        const canPlaySound = computed(
            (): boolean => store.getters['canPlaySound']
        )
        const isUserInteracted = computed(
            (): boolean => store.getters['isUserInteracted']
        )
        const playerTimeOut = computed(
            (): number => store.getters['playerTimeOut']
        )

        const hasVisibleDialog = computed(
            () => dialogCollection.value.length > 0
        )

        const memberid = computed(() => store.getters['memberid'])

        const token = computed(() => store.getters['token'])
        const serverLine = computed((): number => store.getters['serverLine'])
        const serverLineStr = computed(
            (): string => store.getters['serverLineStr']
        )

        const coin = computed((): number => store.getters['coin'])

        const userInfo = computed((): UserInfo => store.getters['userInfo'])
        const music = computed((): boolean => store.getters['music'])

        watch(
            () => route.name,
            (_n) => {
                if (
                    typeof _n === 'string' &&
                    (_n === ROUTES.LOGIN ||
                        _n === ROUTES.SERVER_SELECTION)
                ) {
                    handleCloseSocket()
                    audioPlayer.StopMusic()
                    closeAllDialogs()
                }

                if (userInfo.value && userInfo.value.firstLogin === 0) {
                    if (
                        typeof _n === 'string' &&
                        _n !== ROUTES.LOGIN &&
                        _n !== ROUTES.SERVER_SELECTION
                    ) {
                        router.push({ name: ROUTES.LOGIN })
                    }
                    const _ev: MessageBoxEvent = {
                        type: MessageBoxActionType.open,
                        name: 'required-change-password'
                    }
                    emitter.emit(EventBuss.DIALOG, _ev)
                }
            }
        )

        return {
            isMobile,
            appRoot,
            showLoading,
            toastMessage,
            toastMessageCounter,
            hasVisibleDialog,
            showLanguageDialogBox,
            showSettingDialogBox,
            showSecurityDialogBox,
            showAgreeDialogBox,
            showChipsDialogBox,
            showDemoLogin,
            showRuleDialogBox,
            showRecordDialogBox,
            showMessageDialogBox,
            showMessageDialogTipBox,
            showMessageDialogTipBoxConcent,
            showMessageDialogVipBox,
            showMessageDialogMediaSettingBox,
            showRequiredChangePassword,
            showMessageDialogPrompt,
            showMainView,
            showGamePlayDialogBox,
            requestFullScreen,
            initSound
        }
    }
})
